/**
 * context/ShellContext
 * ──────────────────────────────────────────────────────────────────────────
 * The application shell's state: which view is open, whether the sidebar is
 * collapsed, which assignment's ledger drawer is open, and which modal is up.
 *
 * WHY A SINGLE MODAL SLOT, NOT A BOOLEAN PER DIALOG
 * There are thirteen dialogs. Thirteen booleans is thirteen ways to have two
 * modals open at once. One discriminated union makes that unrepresentable, and
 * it carries the modal's own arguments with it — `{ kind: 'reverse', txnId }`
 * rather than a separate `reversingTxnId` that can drift out of step with the
 * flag that opens the dialog.
 *
 * `dataVersion` is the refetch signal. A write in one place invalidates data in
 * several others: recording a spend changes the register's balance, the
 * Overview's KPI and possibly the approval queue. Rather than have every screen
 * subscribe to every mutation, a write calls `invalidate()` and screens refetch
 * when the number changes. One integer, no cache to keep coherent.
 * ──────────────────────────────────────────────────────────────────────────
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import type { ListKind } from '../schemas/config';
import type { Scope, TxnType } from '../schemas/cash';
import { resolveView, type ViewKey } from '../navigation/routes';
import { useConfig } from './ConfigContext';

/* ── Modals ──────────────────────────────────────────────────────────────── */

export type ModalState =
  | null
  /** Assign cash — the header's primary action. */
  | { kind: 'assign'; scope?: Scope }
  /** Log spend / Collection / Deposit / Replenish, against one assignment. */
  | { kind: 'transaction'; assignmentId: string; txnType: TxnType }
  /** Append a reversing entry for one ledger row. */
  | { kind: 'reverse'; assignmentId: string; txnId: string }
  | { kind: 'transfer'; assignmentId: string }
  | { kind: 'count'; assignmentId: string }
  | { kind: 'closePurpose'; assignmentId: string }
  /** Route or waive one reconciliation variance. */
  | { kind: 'resolveVariance'; reconId: string; action: 'route' | 'waive' }
  /** Reject an approval — the reason is mandatory (invariant 5). */
  | { kind: 'reject'; approvalId: string }
  | { kind: 'settle'; separationId: string }
  | { kind: 'category' }
  | { kind: 'approvalPolicy' }
  | { kind: 'approvalRule' }
  | { kind: 'optionValue'; listKind: ListKind };

/* ── Context ─────────────────────────────────────────────────────────────── */

export interface ShellContextValue {
  view: ViewKey;
  setView: (view: ViewKey) => void;

  navCollapsed: boolean;
  toggleNav: () => void;
  /** Overlay nav on a narrow viewport — a separate concern from `collapsed`. */
  navOpen: boolean;
  setNavOpen: (open: boolean) => void;

  /** The assignment whose ledger drawer is open, or null. */
  drawerAssignmentId: string | null;
  openDrawer: (assignmentId: string) => void;
  closeDrawer: () => void;

  modal: ModalState;
  openModal: (modal: NonNullable<ModalState>) => void;
  closeModal: () => void;

  /** Bumped by every write. Screens refetch when it changes. */
  dataVersion: number;
  invalidate: () => void;
}

const NAV_COLLAPSED_KEY = 'sk_company_cash.nav_collapsed';

const ShellContext = createContext<ShellContextValue | null>(null);

export function ShellProvider({ children }: { children: ReactNode }) {
  const { console: role } = useConfig();

  const [view, setViewState] = useState<ViewKey>(() => resolveView(role, null));
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [drawerAssignmentId, setDrawerAssignmentId] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState>(null);
  const [dataVersion, setDataVersion] = useState(0);

  // The console can change under us — a permissions change, or simply the
  // bootstrap landing after first render. Re-resolve rather than stranding the
  // user on a view their console does not have.
  useEffect(() => {
    setViewState((current) => resolveView(role, current));
  }, [role]);

  // Collapse state is a preference, so it survives a reload.
  useEffect(() => {
    void AsyncStorage.getItem(NAV_COLLAPSED_KEY).then((stored) => {
      if (stored === 'true') setNavCollapsed(true);
    });
  }, []);

  const toggleNav = useCallback(() => {
    setNavCollapsed((collapsed) => {
      const next = !collapsed;
      // Fire and forget: a failed write costs a preference, never a render.
      void AsyncStorage.setItem(NAV_COLLAPSED_KEY, String(next));
      return next;
    });
  }, []);

  const setView = useCallback((next: ViewKey) => {
    setViewState(next);
    // Navigating away closes anything layered over the page. A drawer left
    // open across a view change would describe a row that is no longer there.
    setDrawerAssignmentId(null);
    setModal(null);
    setNavOpen(false);
  }, []);

  const openDrawer = useCallback((assignmentId: string) => {
    setDrawerAssignmentId(assignmentId);
  }, []);

  const closeDrawer = useCallback(() => setDrawerAssignmentId(null), []);

  const openModal = useCallback((next: NonNullable<ModalState>) => setModal(next), []);
  const closeModal = useCallback(() => setModal(null), []);

  const invalidate = useCallback(() => setDataVersion((version) => version + 1), []);

  const value = useMemo<ShellContextValue>(
    () => ({
      view,
      setView,
      navCollapsed,
      toggleNav,
      navOpen,
      setNavOpen,
      drawerAssignmentId,
      openDrawer,
      closeDrawer,
      modal,
      openModal,
      closeModal,
      dataVersion,
      invalidate,
    }),
    [
      view,
      setView,
      navCollapsed,
      toggleNav,
      navOpen,
      drawerAssignmentId,
      openDrawer,
      closeDrawer,
      modal,
      openModal,
      closeModal,
      dataVersion,
      invalidate,
    ],
  );

  return <ShellContext.Provider value={value}>{children}</ShellContext.Provider>;
}

export function useShell(): ShellContextValue {
  const value = useContext(ShellContext);
  if (!value) throw new Error('useShell must be used inside <ShellProvider>');
  return value;
}
